<template>

    <RadioGroup v-model="selectedPlan" class="mt-3">

        <RadioGroupLabel class="sr-only">Pricing plans</RadioGroupLabel>

        <div class="relative -space-y-px shadow rounded-md bg-white">

            <RadioGroupOption as="template" v-for="(plan, planIdx) in availablePlans" :key="plan.name" :value="plan"
                v-slot="{ checked, active }" :disabled="plan.disabled">

                <div :class="[
                    planIdx === 0 ? 'rounded-tl-md rounded-tr-md' : '',
                    planIdx === availablePlans.length - 1 ? 'rounded-bl-md rounded-br-md' : '',
                    checked || checkSuggested(plan) ? 'z-10 border-blue-200 bg-blue-50' : 'border-gray-200',
                    (plan.monthly_id == null && plan.product_id != null) || plan.disabled ? '' : 'cursor-pointer',
                    'relative flex flex-col border focus:outline-none']">

                    <div :class="[
                        checked && plan.options.tiered ? 'py-3' : 'py-4 ',
                        'flex px-4 focus:outline-none md:pl-4 md:pr-6 items-stretch gap-x-4']">

                        <span class="flex items-center text-sm flex-shrink" :class="{ 'opacity-50': plan.disabled }">
                            <RadioGroupLabel as="span"
                                :class="[checked ? 'text-blue-900' : 'text-gray-500', ' font-bold text-md uppercase flex items-center gap-x-1']"
                                v-tippy="{ content: plan.features.join('<br />'), allowHTML: true }">

                                <span>{{ plan.name }}</span>

                                <InformationCircleIcon class="w-4 h-4 inline" />

                                <span v-if="plan.is_current_plan || checkSuggested(plan)"
                                    class="bg-birdie-mauve text-birdie-violet text-xs lowercase font-normal ml-3 px-1 py-0.5 rounded"
                                    v-html="is_free_trial ? 'free trial' : 'current plan'"></span>

                            </RadioGroupLabel>
                        </span>

                        <div v-if="plan.disabled && !is_free_trial" class="flex flew-grow">
                            <span class="bg-gray-200 text-birdie-grey text-xs lowercase font-normal px-1 py-0.5 rounded"
                                v-tippy="{ content: 'Your support team exceed the 10-agent limit of the Plus plan.  Please consider the Business plan for larger teams.' }">unavailable</span>
                        </div>


                        <RadioGroupDescription as="span" :class="[
                            checked ? 'text-blue-700' : 'text-gray-500',
                            'ml-6 pl-1 text-sm md:ml-0 md:pl-0 flex-grow']">


                            <div v-if="plan.options.tiered && plan.monthly_id != null" class="">



                                <div v-if="checked"
                                    class="custom-number-input h-7 flex justify-center items-center gap-x-2">

                                    <div class="flex flex-row h-7 gap-x-2">

                                        <button data-action="decrement"
                                            class=" text-blue-800 hover:text-blue-600 h-full w-6 cursor-pointer outline-none flex items-center justify-center"
                                            @click="decrementSeats(plan)">
                                            <MinusCircleIcon class="w-5 h-5" />
                                        </button>

                                        <div
                                            class="text-center flex items-center justify-center min-w-8 px-0.5 font-semibold text-md  bg-white rounded">
                                            <span>{{ selectedSeats }}</span>
                                        </div>

                                        <button data-action="decrement"
                                            class=" text-blue-800 hover:text-blue-600 h-full w-6 cursor-pointer outline-none flex items-center justify-center"
                                            @click="incrementSeats(plan)">
                                            <PlusCircleIcon class="w-5 h-5" />
                                        </button>
                                    </div>
                                    <span>seat<span :class="{ 'opacity-0': selectedSeats < 2 }">s</span></span>

                                </div>

                                <div v-else-if="plan.is_current_plan"
                                    class="custom-number-input flex justify-center items-center gap-x-2 ">
                                    <div
                                        class="text-center flex items-center justify-center gap-x-1 w-8 font-semibold text-md pl-5">
                                        <span>{{ plan.is_current_plan.quantity }}</span>
                                        <span>seat<span
                                                :class="{ 'opacity-0': plan.is_current_plan.quantity < 2 }">s</span></span>
                                    </div>
                                </div>


                            </div>

                        </RadioGroupDescription>


                        <span v-if="plan.monthly_id != null || plan.product_id == null"
                            class="flex items-center text-sm justify-end gap-x-3 "
                            :class="{ 'opacity-50': plan.disabled }" v-tippy="{
                                content: plan.disabled && !is_free_trial ? 'Your support team exceed the 10-agent limit of the Plus plan.  Please consider the Business plan for larger teams.' : plan.disabled && is_free_trial ? 'Free plan will automatically apply when trial ends' : null
                            }">

                            <span :class="[checked ? 'text-blue-900' : 'text-gray-900', 'font-medium']">
                                <span
                                    v-html="billPlan == 'monthly' ? monthlyDisplay(plan) : yearlyDisplay(plan)"></span>
                                <span v-if="plan.options.tiered"> / seat</span>
                                <span> / mo</span>
                            </span>

                            <span :class="[
                                checked ? 'bg-blue-600 border-transparent' : 'bg-white border-gray-300',
                                active ? 'ring-2 ring-offset-2 ring-blue-600' : '',
                                'h-4 w-4 rounded-full border flex items-center justify-center']" aria-hidden="true">
                                <span class="rounded-full bg-white w-1.5 h-1.5"></span>
                            </span>

                        </span>

                        <span v-else class="flex items-center text-sm justify-end gap-x-3 flex-grow flex-shrink-0 ">
                            <a href="mailto:hey@birdie.so?subject=Upgrade to Enterprise" title="Contact us"
                                class="text-birdie-blue hover:bg-opacity-10 text-medium bg-birdie-blue bg-opacity-5 px-2 py-0.5 rounded border border-birdie-blue">Contact
                                us</a>
                        </span>

                    </div>

                </div>

            </RadioGroupOption>

        </div>

    </RadioGroup>

</template>

<script>

import { InformationCircleIcon } from "@heroicons/vue/20/solid";
import { MinusCircleIcon, PlusCircleIcon } from "@heroicons/vue/24/outline";
import { RadioGroup, RadioGroupDescription, RadioGroupLabel, RadioGroupOption } from '@headlessui/vue'

export default {
    props: ["available_plans", "selected_plan", "type", "billPlan", "active_seats", "is_free_trial"],
    components: {
        MinusCircleIcon, PlusCircleIcon, InformationCircleIcon,
        RadioGroup, RadioGroupDescription, RadioGroupLabel, RadioGroupOption
    },
    data() {
        return {
            selectedPlan: this.selected_plan,
            selectedSeats: 1,
            availablePlans: this.available_plans,
            suggestedPlan: null
        }
    },
    created() {

        if (this.selectedPlan.is_current_plan) {
            if (this.selectedPlan.options.tiered) {
                this.selectedSeats = Math.max(1, this.selectedPlan.is_current_plan.quantity)
            }
        }

        if (this.is_free_trial) {
            for (var i = 0; i < this.availablePlans.length; i++) {
                if (this.availablePlans[i].product_id == null) {
                    this.availablePlans[i].disabled = true
                    this.availablePlans[i].is_current_plan = false
                    this.selectedPlan = null
                }
                if (i == this.availablePlans.length - 1) {
                    this.suggestedPlan = this.availablePlans[i]
                    this.selectedPlay = this.availablePlans[i]
                }
            }
        }

        // console.log(this.is_free_trial, this.selectedPlan, this.availablePlans)
        // this.monthlyDisplay = this.plan.price.monthly
        // if (this.plan.currency == 'usd') {
        //     this.monthlyDisplay = '$'+this.plan.price.monthly
        // } else {
        //     this.monthlyDisplay = this.plan.price.monthly+'€'
        // }

        // this.yearlyDisplay = this.plan.price.yearly_round
        // if (this.plan.currency == 'usd') {
        //     this.yearlyDisplay = '$'+this.plan.price.yearly_round
        // } else {
        //     this.yearlyDisplay = this.plan.price.yearly_round+'€'
        // }

        // console.log(this.plan)

    },
    methods: {
        monthlyDisplay: function (plan) {
            if (plan.currency == 'usd') {
                return '$' + plan.price.monthly
            } else {
                return plan.price.monthly + '€'
            }
        },
        yearlyDisplay: function (plan) {
            if (plan.currency == 'usd') {
                return '$' + plan.price.yearly_round
            } else {
                return plan.price.yearly_round + '€'
            }
        },
        incrementSeats: function (plan) {
            this.selectedSeats++
            this.$emit('changeSeats', this.selectedSeats, plan)
            //this.computeFullPrice()
        },
        decrementSeats: function (plan) {
            if (this.selectedSeats <= 1) {
                return
            }
            if (this.selectedSeats <= this.active_seats) {
                let options = {
                    type: "info",
                    timeout: -1,
                    position: "top-right",
                    toastBackgroundColor: '#207FFF',
                    // hideProgressBar: false,
                }
                this.$moshaToast({
                    title: `Before downgrading your plan, ensure that you have disabled the correct number of seats.`,
                    description: `<a href='${route('teams.show', this.$page.props.user.current_team_id)}' title='Upgrade'>Go to your team settings →</a>`
                }, options)
                return
            }
            this.selectedSeats--
            this.$emit('changeSeats', this.selectedSeats, plan)
            //this.computeFullPrice()
        },
        checkSuggested: function (plan) {
            return this.suggestedPlan?.name == plan.name
        }
    },
    watch: {
        selectedPlan: function (val, oldVal) {
            this.$emit('changePlan', this.type, val)
        },

    },
    computed: {

    }
}
</script>
